import React from 'react';
import { useTranslation } from 'react-i18next';
import PendingGuestReview from './PendingGuestReview';
import Chat from './Chat';

// --- General List/Card Styles ---
const LIST_SCROLL_STYLE = { maxHeight: '350px', overflowY: 'auto', paddingRight: '10px' };

export default function ReceptionDashboard({
    hotelName,
    pendingRegistrationCount,
    handleAcceptPreBooking,
    handleRejectPreBooking,

    // Defensive coding fix: Default to empty arrays/objects to prevent crashes when data is missing
    pendingGuestRequests = [], 
    handleResolveGuestRequest,

    doNotDisturbRooms,
    updateGuestStatus,

    lostItems = [],
    newLostItem = {}, 
    setNewLostItem,
    handleRegisterLostItem,
    handleUpdateLostItemStatus,

    inventoryItems = [],
    newInventoryItem = {},
    
    setNewInventoryItem,
    handleAddNewInventoryItem,
    handleUpdateInventory,

    stayModificationRequests = [], 
    handleResolveStayRequest,

    checkoutRequests = [], 
    handleFinalizeCheckout,
    
    // Billing Props
    openBillGuestId,
    toggleBillForm, 
    currentBillDetails,
    handleBillChange,
    handleAddItemToBill,
    handleRemoveBillItem,

    activeGuests = [], 
    handleNotifyGuest,

    // Chat Props
    selectedChatId,
    activeChats = [], 
    handleSelectChat,
    handleCloseChat,
    handleDeleteChat,
    unreadChatCount,
    isMaximized,
    handleMaximizeToggle,
    guestDataForChat,
    selectedChat,

    // Room/Settings Props
    rooms = [], 
    newRoom = {}, 
    handleRoomChange,
    handleAddRoom,
    handleDeleteRoom,
    hotelId,
    updateRoomStatus,
    hotelSettings = {}, 
    setHotelSettings,
    handleSaveSettings,

    guestReviews = [], 
    error,
    success,
    loading
}) {
    const { t } = useTranslation();
    
    // --- Helper Functions ---
    const getDisplayDate = (dateField) => {
        if (!dateField) return 'N/A';
        const date = typeof dateField.toDate === 'function' ? dateField.toDate() : dateField;
        if (date instanceof Date && !isNaN(date)) {
            return date.toLocaleDateString();
        }
        return 'N/A';
    };

    const formatStatus = (status) => {
        if (!status) return 'N/A';
        const statusMap = {
            'available': t('reception.statuses.available'),
            'occupied': t('reception.statuses.occupied'),
            'DO_NOT_DISTURB': t('reception.statuses.do_not_disturb'),
            'Needs Cleaning': t('reception.statuses.needs_cleaning'),
            'Under Maintenance': t('reception.statuses.under_maintenance'),
            'Out of Service': t('reception.statuses.out_of_service'),
            'Inspecting': t('reception.statuses.inspecting'),
            'Cleaning In Progress': t('reception.statuses.cleaning_in_progress')
        };
        return statusMap[status] || status.replace(/_/g, ' ').toUpperCase();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'available': return 'var(--color-success)';
            case 'Needs Cleaning': return 'var(--color-error)';
            case 'Cleaning In Progress': return 'orange';
            case 'Cleaning En Route': return 'blue';
            case 'Under Maintenance': return 'gray';
            case 'Inspecting': return '#8a2be2';
            case 'Out of Service': return '#8b0000';
            case 'occupied': return 'var(--color-primary)';
            case 'DO_NOT_DISTURB': return '#ff4500';
            default: return 'var(--color-secondary)';
        }
    };

    return (
        <div style={{ marginBottom: '60px' }}>
            {/* Header Card */}
            <div className="card">
                <h2>{hotelName || t('reception.default_staff')} — {t('reception.title')}</h2>
                <p style={{ color: 'var(--color-secondary)' }}>
                    {t('reception.managing_ops')}: <strong>{hotelName || t('reception.not_assigned')}</strong>
                </p>
                {error && <div className="alert-error">{error}</div>}
                {success && <div className="alert-success">{success}</div>}
            </div>

            {/* Pending Digital Check-in Requests */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                📝 {t('reception.checkin_requests')} ({pendingRegistrationCount || 0}) 
            </div>

            <div className="card" style={{ marginBottom: '20px', border: '1px solid var(--color-warning)' }}>
                <div style={LIST_SCROLL_STYLE}>
                    <PendingGuestReview
                        pendingCount={pendingRegistrationCount}
                        onAccept={handleAcceptPreBooking}
                        onReject={handleRejectPreBooking}
                    />
                </div>
            </div>

            {/* Guest Service Requests */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                🛎️ {t('reception.service_requests')} ({pendingGuestRequests.length})
            </div>

            <div className="card" style={{ marginBottom: '20px', border: '1px solid var(--color-error)' }}>
                <div style={LIST_SCROLL_STYLE}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0' }}>
                        {pendingGuestRequests.length > 0 ? (
                            pendingGuestRequests.map((req, index) => {
                                if (!req) return null;
                                const room = req.roomNumber ?? "N/A";
                                const name = req.fullName ?? "Unknown Guest";
                                const type = req.requestType ?? t('reception.request');
                                const details = req.requestDetails ?? "No details";
                                const time = req.requestTimeDisplay ?? "...";

                                return (
                                    <li key={req.id || index} className="list-item" style={{
                                        borderLeft: '5px solid var(--color-error)',
                                        paddingLeft: '10px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '10px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <div style={{ flexGrow: 1 }}>
                                            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
                                                {type} {t('reception.from')} {name} ({t('reception.room')} {room})
                                            </p>
                                            <small style={{ color: '#555', display: 'block', marginBottom: '5px' }}>
                                                {t('reception.details')}: {details}
                                            </small>
                                            <small style={{ color: 'var(--color-error)' }}>
                                                {t('reception.time')}: {time}
                                            </small>
                                        </div>
                                        <div style={{ minWidth: '150px', textAlign: 'right' }}>
                                            <button
                                                onClick={() => handleResolveGuestRequest(req.id, type)}
                                                className="btn-primary"
                                                style={{ backgroundColor: 'var(--color-success)', color: 'white', padding: '8px 15px' }}
                                                disabled={loading}
                                            >
                                                {t('reception.resolve')}
                                            </button>
                                        </div>
                                    </li>
                                );
                            })
                        ) : (
                            <p style={{ margin: 0, padding: '10px 0', color: 'var(--color-secondary)' }}>
                                {t('reception.no_service_requests')}
                            </p>
                        )}
                    </ul>
                </div>
            </div>

            {/* Stay Modification Requests */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                📝 {t('reception.modification_requests')} ({stayModificationRequests.length})
            </div>

            <div className="card" style={{ marginBottom: '20px', border: '1px solid var(--color-accent)' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0', ...LIST_SCROLL_STYLE }}> 
                    {stayModificationRequests.length > 0 ? (
                        stayModificationRequests.map(request => (
                            <li key={request.id} className="list-item" style={{
                                borderLeft: '5px solid var(--color-accent)',
                                paddingLeft: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '10px',
                                flexWrap: 'wrap'
                            }}>
                                <div style={{ flexGrow: 1 }}>
                                    <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
                                        {request.fullName} — {t('reception.room')} {request.roomNumber}
                                    </p>
                                    <small style={{ display: 'block', marginBottom: '5px', color: '#555' }}>
                                        <b>{t('reception.request')}:</b> {
        request.type 
        ? request.type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) 
        : t('common.na')
    }
                                    </small>
                                    <small style={{ color: 'var(--color-accent)' }}>
                                        {t('reception.reason')}: {request.reason || 'N/A'}
                                    </small>
                                </div>
                                <div style={{ minWidth: '200px', textAlign: 'right' }}>
                                    <button
                                        onClick={() => handleResolveStayRequest(request.id)}
                                        className="btn-primary"
                                        style={{ padding: '8px 15px' }}
                                        disabled={loading}
                                    >
                                        {t('reception.approve')}
                                    </button>
                                </div>
                            </li>
                        ))
                    ) : (
                        <p style={{ margin: 0, padding: '10px 0', color: 'var(--color-secondary)' }}>
                            {t('reception.no_mod_requests')}
                        </p>
                    )}
                </ul>
            </div>

            {/* Guests Requesting Check-out */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                💰 {t('reception.checkout_requests')} ({checkoutRequests.length})
            </div>

            <div className="card" style={{ marginBottom: '20px', border: '1px solid var(--color-accent)' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0' }}>
                    {checkoutRequests.length > 0 ? (
                        checkoutRequests.map(guest => (
                            <li key={guest.id} className="list-item" style={{
                                borderLeft: '5px solid var(--color-accent)',
                                paddingLeft: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '10px',
                                flexWrap: 'wrap',
                                flexDirection: 'column'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', width: '100%' }}>
                                    <div style={{ flexGrow: 1, marginBottom: '10px' }}>
                                        <p style={{ margin: 0, fontWeight: 'bold' }}>
                                            {guest.fullName} — {t('reception.room')} {guest.roomNumber}
                                        </p>
                                        <small>{t('reception.checkin')}: {getDisplayDate(guest.checkInDate)}</small>
                                        <p style={{ margin: '5px 0 0 0' }}>
                                            <b>{t('reception.addons_total')}:</b> ${guest.addOnsPrice ? guest.addOnsPrice.toFixed(2) : '0.00'}
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '350px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                        <button
                                            onClick={() => toggleBillForm(guest.id)}
                                            className="btn-secondary"
                                            style={{ backgroundColor: openBillGuestId === guest.id ? 'orange' : 'gray', color: 'white', padding: '8px 15px' }}
                                            disabled={loading}
                                        >
                                            {openBillGuestId === guest.id ? t('reception.close_bill') : t('reception.fill_edit_bill')}
                                        </button>
                                        <button
                                            onClick={() => handleFinalizeCheckout(guest.id, guest.roomId)}
                                            className="btn-primary"
                                            style={{ backgroundColor: 'var(--color-accent)', color: 'white', padding: '8px 15px' }}
                                            disabled={loading}
                                        >
                                            {t('reception.finalize_invoice')}
                                        </button>
                                    </div>
                                </div>
                                
                                {openBillGuestId === guest.id && (
                                    <div style={{ marginTop: '15px', padding: '15px', border: '1px dashed #ccc', borderRadius: '5px', width: '100%' }}>
                                        <h4>{t('reception.add_item_title')} ({t('reception.room')} {guest.roomNumber})</h4>
                                        <form onSubmit={(e) => { e.preventDefault(); handleAddItemToBill(guest); }}>
                                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                                <input
                                                    type="text"
                                                    name="description"
                                                    value={currentBillDetails.description || ''}
                                                    onChange={handleBillChange}
                                                    placeholder={t('reception.bill_desc_placeholder')}
                                                    required
                                                    style={{ flex: 2, padding: '8px' }}
                                                />
                                                <input
                                                    type="number"
                                                    name="amount"
                                                    value={currentBillDetails.amount || ''}
                                                    onChange={handleBillChange}
                                                    placeholder={t('reception.bill_amount_placeholder')}
                                                    required
                                                    min="0.01"
                                                    step="0.01"
                                                    style={{ flex: 1, padding: '8px' }}
                                                />
                                                <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1, padding: '8px' }}>
                                                    {t('reception.add_item')}
                                                </button>
                                            </div>
                                        </form>

                                        {guest.addOnsDetails && guest.addOnsDetails.length > 0 && (
                                            <div style={{ marginTop: '15px' }}>
                                                <p style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>{t('reception.current_addons')}:</p>
                                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                    {guest.addOnsDetails.map((item, index) => (
                                                        <li key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px dotted #eee' }}>
                                                            <span>{item.description}</span>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                ${item.amount.toFixed(2)}
                                                                <button
                                                                    onClick={() => handleRemoveBillItem(guest, index)}
                                                                    style={{ background: 'none', border: 'none', color: 'darkred', cursor: 'pointer', fontSize: '0.9em' }}
                                                                    disabled={loading}
                                                                >
                                                                    ({t('reception.remove')})
                                                                </button>
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </li>
                        ))
                    ) : (
                        <p style={{ margin: 0, padding: '10px 0', color: 'var(--color-secondary)' }}>
                            {t('reception.no_checkout_requests')}
                        </p>
                    )}
                </ul>
            </div>

            {/* Active Guests */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                🛏️ {t('reception.active_guests_title')} ({activeGuests.length})
            </div>

            <div className="card" style={{ marginBottom: '20px', border: '1px solid var(--color-primary)' }}>
                <h4 style={{ marginTop: 0, color: 'var(--color-primary)' }}>{t('reception.currently_checked_in')}</h4>
                <div style={LIST_SCROLL_STYLE}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0' }}>
                        {activeGuests.length > 0 ? (
                            activeGuests.map(guest => (
                                <li key={guest.id} className="list-item" style={{
                                    borderLeft: '5px solid var(--color-primary)',
                                    paddingLeft: '10px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '10px',
                                    flexWrap: 'wrap'
                                }}>
                                    <div style={{ flexGrow: 1 }}>
                                        <p style={{ margin: 0, fontWeight: 'bold' }}>
                                            {guest.fullName} - {t('reception.room')} {guest.roomNumber}
                                        </p>
                                        <small>{t('reception.checkin')}: {getDisplayDate(guest.checkInDate)}</small>
                                    </div>
                                    <div style={{ minWidth: '450px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => handleNotifyGuest(guest)}
                                            className="btn-primary"
                                            style={{ backgroundColor: 'blue', color: 'white', padding: '8px 15px' }}
                                            disabled={loading}
                                        >
                                            {t('reception.send_cleaning_eta')}
                                        </button>
                                        <button
                                            onClick={() => updateGuestStatus(guest.id, 'CHECKOUT_REQUESTED')}
                                            className="btn-secondary"
                                            style={{ backgroundColor: 'var(--color-accent)', color: 'white', padding: '8px 15px' }}
                                            disabled={loading || guest.status === 'CHECKOUT_REQUESTED'}
                                        >
                                            {t('reception.request_checkout_btn')}
                                        </button>
                                    </div>
                                </li>
                            ))
                        ) : (
                            <p style={{ margin: 0, padding: '10px 0', color: 'var(--color-secondary)' }}>
                                {t('reception.no_active_guests')}
                            </p>
                        )}
                    </ul>
                </div>
            </div>

            {/* Chat Hub */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                💬 {t('reception.chat_hub_title')} ({activeChats.length})
            </div>

            <div className="card" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px', height: '550px' }}>
                {/* Chat List Elevator */}
                <div style={{ borderRight: '1px solid #ccc', padding: '10px', overflowY: 'auto' }}>
                    <h4>{t('reception.conversations')}</h4>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {activeChats.length > 0 ? (
                            activeChats.map(chat => (
                                <li
                                    key={chat.id}
                                    onClick={() => handleSelectChat(chat.id)}
                                    style={{
                                        padding: '10px',
                                        cursor: 'pointer',
                                        backgroundColor: selectedChatId === chat.id ? '#f0f0f0' : 'white',
                                        borderBottom: '1px solid #eee',
                                        borderLeft: chat.isUnread ? '3px solid red' : 'none',
                                        marginBottom: '5px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    <p style={{ margin: 0, fontWeight: 'bold' }}>
                                        {chat.guestName} ({t('reception.room')} {chat.roomNumber})
                                    </p>
                                    <small>
                                        {chat.lastMessageTime ? new Date(chat.lastMessageTime).toLocaleTimeString() : t('reception.chat.no_time')}
                                    </small>
                                    <p style={{ fontSize: '0.9em', marginTop: '5px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {chat.lastMessageText || t('reception.chat.no_messages')}
                                    </p>
                                </li>
                            ))
                        ) : (
                            <p style={{ padding: '10px', textAlign: 'center', color: 'var(--color-secondary)' }}>
                                {t('reception.no_active_chats')}
                            </p>
                        )}
                    </ul>
                </div>

                {/* Chat Window Elevator */}
                <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                    {selectedChatId ? (
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div style={{ flexGrow: 1, overflowY: 'auto', border: '1px solid #eee', borderRadius: '5px' }}>
                                <Chat
                                    currentChatId={selectedChatId}
                                    isStaff={true}
                                    hotelId={hotelId}
                                    guestData={guestDataForChat}
                                    isMaximized={isMaximized}
                                    toggleMaximize={handleMaximizeToggle}
                                    closeChat={() => handleCloseChat()}
                                />
                            </div>
                            <button
                                onClick={() => handleDeleteChat(selectedChatId, selectedChat?.guestName, selectedChat?.roomNumber)}
                                style={{
                                    marginTop: '10px',
                                    width: '100%',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('reception.delete_conversation')}
                            </button>
                        </div>
                    ) : (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px dashed #ccc',
                            borderRadius: '5px'
                        }}>
                            <p style={{ color: 'var(--color-secondary)' }}>
                                {t('reception.select_chat_prompt')}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Settings */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                ⚙️ {t('reception.automation_title')}
            </div>

            <div className="card" style={{ border: '1px solid #dee2e6', padding: '20px', backgroundColor: 'var(--color-background)', marginBottom: '40px' }} >
                <h3 style={{ marginTop: 0, color: 'var(--color-primary)' }}> {t('reception.edit_automated_content')} </h3>
                <form onSubmit={handleSaveSettings}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                        <div>
                            <label htmlFor="wifiSSID">{t('reception.wifi_ssid_label')}</label>
                            <input
                                id="wifiSSID"
                                type="text"
                                value={hotelSettings.wifiSSID || ''}
                                onChange={(e) => setHotelSettings(prev => ({ ...prev, wifiSSID: e.target.value }))}
                                placeholder="e.g., HotelGuestWifi"
                                style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div>
                            <label htmlFor="wifiPassword">{t('reception.wifi_password_label')}</label>
                            <input
                                id="wifiPassword"
                                type="text"
                                value={hotelSettings.wifiPassword || ''}
                                onChange={(e) => setHotelSettings(p => ({ ...p, wifiPassword: e.target.value }))}
                                placeholder="e.g., welcome123"
                                style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
                            />
                        </div>
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="housekeepingMessage">{t('reception.housekeeping_msg_label')}</label>
                        <input
                            id="housekeepingMessage"
                            type="text"
                            value={hotelSettings.housekeepingMessage || ''}
                            onChange={(e) => setHotelSettings(p => ({ ...p, housekeepingMessage: e.target.value }))}
                            placeholder="e.g., 5 minutes"
                            style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
                        />
                    </div>
                    <button type="submit" className="btn-primary" disabled={loading}> 
                        {t('reception.save_settings')} 
                    </button>
                </form>
            </div>

            {/* Room Inventory */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                🔑 {t('reception.inventory_title')} ({rooms.length} {t('reception.total_rooms')})
            </div>

            <div className="card" style={{ display: 'flex', gap: '20px', marginBottom: '40px', flexWrap: 'wrap' }} >
                <div style={{ flex: 1, minWidth: '300px', padding: '10px', border: '1px solid #ccc', borderRadius: '5px' }} >
                    <h4>{t('reception.add_room_title')}</h4>
                    <form onSubmit={(e) => { e.preventDefault(); handleAddRoom(e); }} style={{ display: 'grid', gap: '10px' }}>
                        <input 
                            type="text" 
                            name="roomNumber" 
                            value={newRoom.roomNumber || ''}
                            onChange={handleRoomChange} 
                            placeholder={t('reception.room_num_placeholder')} 
                            required 
                            style={{ padding: '10px' }} 
                        />
                        <input 
                            type="number" 
                            name="price" 
                            value={newRoom.price || ''}
                            onChange={handleRoomChange} 
                            placeholder={t('reception.price_placeholder')} 
                            required 
                            step="0.01" 
                            min="0" 
                            style={{ padding: '10px' }} 
                        />
                        <select 
                            name="type" 
                            value={newRoom.type || 'Standard'}
                            onChange={handleRoomChange} 
                            style={{ padding: '10px' }} 
                        >
                            <option value="Standard">{t('reception.room_types.standard')}</option>
                            <option value="Deluxe">{t('reception.room_types.deluxe')}</option>
                            <option value="Suite">{t('reception.room_types.suite')}</option>
                        </select>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {t('reception.add_room_btn')}
                        </button>
                    </form>
                </div>
                
                <div style={{ flex: 2, minWidth: '400px', padding: '10px', border: '1px solid #ccc', borderRadius: '5px' }} >
                    <h4>{t('reception.room_status_overview')}</h4>
                    <div style={LIST_SCROLL_STYLE}>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {rooms
                                .sort((a, b) => (a?.roomNumber || "").localeCompare(b?.roomNumber || "", undefined, { numeric: true }))
                                .map(room => (
                                    room ? ( 
                                        <li key={room.id} className="list-item" style={{ 
                                                borderLeft: `5px solid ${getStatusColor(room.status)}`, 
                                                paddingLeft: '10px', 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                marginBottom: '10px', 
                                                flexWrap: 'wrap' 
                                            }} 
                                        >
                                            <div style={{ flexGrow: 1 }}>
                                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}> 
                                                    {t('reception.room')} {room.roomNumber || "N/A"} ({room.type || "Unknown"}) 
                                                </p>
                                                <small style={{ color: getStatusColor(room.status) }}> 
                                                    {t('reception.status')}: {formatStatus(room.status)} 
                                                    {room.currentGuestId && ` (${t('reception.guest_id')}: ${room.currentGuestId.substring(0, 5)}...)`} 
                                                </small>
                                            </div>
                                            <div style={{ minWidth: '400px', textAlign: 'right', display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}> 
                                                <select 
                                                    value={room.status} 
                                                    onChange={(e) => updateRoomStatus(room.id, e.target.value)} 
                                                    style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '150px' }} 
                                                    disabled={loading} 
                                                >
                                                    <option value="available">{t('reception.statuses.available')}</option> 
                                                    <option value="occupied" disabled>{t('reception.statuses.occupied')}</option> 
                                                    <option value="DO_NOT_DISTURB">{t('reception.statuses.do_not_disturb')}</option> 
                                                    <option value="Needs Cleaning">{t('reception.statuses.needs_cleaning')}</option> 
                                                    <option value="Under Maintenance">{t('reception.statuses.under_maintenance')}</option>
                                                    <option value="Out of Service">{t('reception.statuses.out_of_service')}</option>
                                                </select>
                                                <button
                                                    onClick={() => handleDeleteRoom(room.id)}
                                                    className="btn-secondary"
                                                    style={{ backgroundColor: 'darkred', color: 'white', padding: '8px 15px' }}
                                                    disabled={loading}
                                                >
                                                    {t('reception.delete')}
                                                </button>
                                            </div>
                                        </li>
                                    ) : null
                                ))}
                        </ul>
                    </div>
                </div>
            </div>

           {/* Guest Reviews */}
            <div className="section-title" style={{ marginTop: '40px' }}>
                ⭐ {t('reception.latest_reviews')} ({guestReviews.length})
            </div>

            <div className="card" style={{ marginBottom: '40px', border: '1px solid var(--color-primary)' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0', ...LIST_SCROLL_STYLE }}> 
                    {guestReviews.length > 0 ? (
                        guestReviews.map(review => (
                            <li key={review.id} className="list-item" style={{
                                    borderLeft: '5px solid #ffc107',
                                    paddingLeft: '10px',
                                    marginBottom: '15px',
                                    padding: '10px',
                                    backgroundColor: '#fff'
                                }}
                            >
                                <p style={{ margin: 0, fontWeight: 'bold' }}>
                                    {t('reception.review_id')}: {review.id.substring(0, 8)} | {t('reception.booking')}: {review.bookingKey ? review.bookingKey.substring(0, 8) : 'N/A'}
                                </p>
                                <small>{t('reception.submitted')}: {review.timestampDisplay}</small>
                                <div style={{ marginTop: '5px', display: 'flex', gap: '20px' }}>
                                    <p><b>{t('reception.review_stay')}:</b> {review.stay}/5</p>
                                    <p><b>{t('reception.review_cleaning')}:</b> {review.cleaning}/5</p>
                                    <p><b>{t('reception.review_staff')}:</b> {review.staff}/5</p>
                                </div>
                                {review.comments && (
                                    <div style={{ marginTop: '10px', borderTop: '1px dashed #eee', paddingTop: '10px' }}>
                                        <p style={{ margin: 0 }}><b>{t('reception.comments')}:</b></p>
                                        <p style={{ margin: 0, fontStyle: 'italic' }}>“{review.comments}”</p>
                                    </div>
                                )}
                            </li>
                        ))
                    ) : (
                        <p style={{ margin: 0, padding: '10px 0', color: 'var(--color-secondary)' }}>
                            {t('reception.no_reviews')}
                        </p>
                    )}
                </ul>
            </div>
        </div>
    );
}